/**
 * non-working-days/api — kalendarz dni wolnych od pracy tenanta,
 * Ustawienia → „Dni wolne od pracy" (mirror `lib/record-procedures/api.ts`).
 *
 * Kalendarz jest TENANT-GLOBALNY (bez `company_id`) — JEDNO uprawnienie
 * `crm_core.non_working_days.manage` bramkuje wszystko (podgląd i zmiany) — patrz
 * `useCanManageNonWorkingDays`. BEZ `withRequireModule` — `crm_core` jest pakietem bazowym.
 *
 * Endpointy (`/api/settings/non-working-days`):
 *   GET    /              — aktywne wpisy `{ items }`; serwer sortuje: najpierw „co roku" wg
 *                           (miesiąc, dzień), potem jednorazowe wg daty
 *   POST   /              — dodaj wpis `{ day, repeatsYearly, name }` → 201 `{ item }`
 *   PATCH  /:id           — edycja częściowa → 200 `{ item }`; 404 `NOT_FOUND`
 *   DELETE /:id           — miękkie usunięcie (gcrecord) → 200
 *   POST   /:id/restore   — przywrócenie → 200 `{ item }` (BEZ UI: ekran nie ma widoku kosza,
 *                           więc klient tego endpointu nie istnieje — dojdzie razem z koszem)
 *
 * Błędy: 409 `NON_WORKING_DAY_DUPLICATE` (ten sam dzień w tym samym trybie powtarzania —
 * unikalność partial po `gcrecord IS NULL`), 400 `VALIDATION_ERROR`, 403 `PERMISSION_DENIED`.
 *
 * Odświeżanie: każda mutacja — także nieudana (np. 404, bo ktoś inny już usunął wpis) —
 * kończy się unieważnieniem listy, a hook zwraca obietnicę odświeżenia z `onSettled`:
 * `mutateAsync` rozwiązuje się/odrzuca dopiero po pobraniu świeżej listy, więc wołający nie widzi
 * w międzyczasie starej wartości wiersza, a ekran po błędzie pokazuje stan z serwera.
 */

import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { api } from '@/lib/api';

// ============================================================================
// Types
// ============================================================================

export interface NonWorkingDayDto {
  id: string;
  /** Data ISO `YYYY-MM-DD`. Dla wpisu „co roku" rok jest dowolny — liczy się dzień i miesiąc. */
  day: string;
  repeatsYearly: boolean;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateNonWorkingDayInput {
  day: string;
  repeatsYearly: boolean;
  name: string;
}

/** Edycja częściowa — serwer zmienia tylko przekazane pola. */
export type UpdateNonWorkingDayInput = Partial<CreateNonWorkingDayInput>;

/** Zmienne mutacji edycji: id wpisu + pola do zmiany. */
export interface UpdateNonWorkingDayVariables {
  id: string;
  values: UpdateNonWorkingDayInput;
}

interface ListResponse {
  items: NonWorkingDayDto[];
}

interface ItemResponse {
  item: NonWorkingDayDto;
}

/** Kod błędu 409 — ten sam dzień jest już na liście (w tym samym trybie powtarzania). */
export const NON_WORKING_DAY_DUPLICATE_CODE = 'NON_WORKING_DAY_DUPLICATE';

/** Czy błąd to kolizja unikalności dnia (409 `NON_WORKING_DAY_DUPLICATE`). */
export function isNonWorkingDayDuplicateError(err: unknown): boolean {
  return (
    isAxiosError<{ error?: string }>(err) &&
    err.response?.status === 409 &&
    err.response.data?.error === NON_WORKING_DAY_DUPLICATE_CODE
  );
}

// ============================================================================
// Query keys
// ============================================================================

export const NON_WORKING_DAYS_LIST_KEY = ['non-working-days'] as const;

// ============================================================================
// Hooks — list
// ============================================================================

export function useNonWorkingDaysList(): UseQueryResult<NonWorkingDayDto[], Error> {
  return useQuery<NonWorkingDayDto[], Error>({
    queryKey: NON_WORKING_DAYS_LIST_KEY,
    queryFn: async (): Promise<NonWorkingDayDto[]> => {
      const res = await api.get<ListResponse>('/settings/non-working-days');
      return res.data.items;
    },
    retry: false,
  });
}

function useRefreshNonWorkingDays(): () => Promise<void> {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: NON_WORKING_DAYS_LIST_KEY });
}

// ============================================================================
// Hooks — mutations (create/update/delete)
// ============================================================================

export function useCreateNonWorkingDay(): UseMutationResult<
  NonWorkingDayDto,
  Error,
  CreateNonWorkingDayInput
> {
  const refresh = useRefreshNonWorkingDays();
  return useMutation<NonWorkingDayDto, Error, CreateNonWorkingDayInput>({
    mutationFn: async (input) => {
      const res = await api.post<ItemResponse>('/settings/non-working-days', input);
      return res.data.item;
    },
    onSettled: refresh,
  });
}

export function useUpdateNonWorkingDay(): UseMutationResult<
  NonWorkingDayDto,
  Error,
  UpdateNonWorkingDayVariables
> {
  const refresh = useRefreshNonWorkingDays();
  return useMutation<NonWorkingDayDto, Error, UpdateNonWorkingDayVariables>({
    mutationFn: async ({ id, values }) => {
      const res = await api.patch<ItemResponse>(`/settings/non-working-days/${id}`, values);
      return res.data.item;
    },
    onSettled: refresh,
  });
}

export function useDeleteNonWorkingDay(): UseMutationResult<string, Error, string> {
  const refresh = useRefreshNonWorkingDays();
  return useMutation<string, Error, string>({
    mutationFn: async (id) => {
      await api.delete(`/settings/non-working-days/${id}`);
      return id;
    },
    onSettled: refresh,
  });
}
